import React from "react";
import NotificationItem from "./NotificationItem";
import { styled } from "@mui/system";
import { dateFormatter } from "src/utils/helpers/DateAndTimeHelpers";
import AlertBlock from "./extra/AlertBlock";
import { NotificationRes } from "src/store/features/api/generated";
import api from "src/store/features/api";
import { useAppDispatch } from "src/store/store";
import { setCurrentNotificationToPrevious, setNotifications } from "src/store/features/user.slice";

interface WrapperProps {
  hide: boolean;
}

const Wrapper = styled("div")<WrapperProps>(({ hide }) => ({
  display: hide ? "none" : "flex",
  flexDirection: "column",
  alignSelf: "stretch",
  padding: "8px 0px",
  gap: "16px",
  maxHeight: "466px",
  overflow: "auto",
  /* Style scrollbar for WebKit (Chrome, Safari) */
  "&::-webkit-scrollbar": {
    width: "5px" /* Width of the scrollbar */,
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "#E5E7E8" /* Color of the scrollbar thumb */,
    borderRadius: "5px" /* Border radius of the scrollbar thumb */,
  },

  "&::-webkit-scrollbar-track": {
    backgroundColor: "transparent" /* Color of the scrollbar thumb */,
  },
}));

const NotifWrapper = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: " flex-start",
  gap: "12px",
}));

const Timestamp = styled("div")(() => ({
  color: "#7B858B",
  fontFamily: "Söhne",
  fontSize: "12px",
  fontStyle: "normal",
  fontWeight: 400,
  lineHeight: "12px",
}));

export interface IGroupedNotifications {
  [timestamp: string]: NotificationRes[];
}

interface NotificationListProps {
  type: "current" | "previous";
  notifications: NotificationRes[];
}

/**
 * A list component to display notifications grouped by timestamp.
 * @param {"current" |"previous"} props.type - A type that indicates which text to use for the AlertBlock component.
 * @param {INotification[]} props.notifications - An array of notifications to display.
 */
const NotificationList = (props: NotificationListProps) => {
  const { type, notifications } = props;

  const dispatch = useAppDispatch();
  const [setNotificationToRead] = api.useSetNotificationsToReadMutation(); // sets a "current" notification to "previous"
  const [clearCurrentNotifications] = api.useClearNotificationsMutation(); // clears all "current" notifications
  const [clearPreviousNotifications] = api.useClearNotificationsMutation(); // clears all or one "previous" notifications

  // const [notificationList, setNotificationList] = React.useState<NotificationRes[]>(notifications);
  const notifLength = notifications.length;
  let alertText = "";
  let alertButtonProps = { text: "clear", onClick: () => {} };
  let showAlertIndicator = false;
  let showAlertBlock = true;

  if (type === "current") {
    alertText = `You have ${notifLength} new notifications`;
    showAlertIndicator = notifLength > 0;
    alertButtonProps.onClick = () => {
      const ids = notifications.map((notif): string => notif.id);
      clearCurrentNotifications({ userNotificationIdsDto: { ids } });
      dispatch(
        setNotifications({
          currentNotifications: [],
          notificationAmount: 0,
          refetchNotifications: true,
        }),
      );
    };
    if (notifLength === 0) {
      alertButtonProps.text = "";
    }
  }

  if (type === "previous") {
    alertText = "Previous notifications";
    alertButtonProps.onClick = () => {
      const ids = notifications.map((notif): string => notif.id);
      clearPreviousNotifications({ userNotificationIdsDto: { ids } });
      dispatch(setNotifications({ previousNotifications: [] }));
    };
    if (notifLength === 0) {
      showAlertBlock = false;
      alertButtonProps.text = "";
    }
  }

  const groupedNotifications = React.useMemo((): IGroupedNotifications => {
    const groupedNotifications: IGroupedNotifications = {};
    // Iterate over the notification list
    notifications.forEach((notification) => {
      const timestamp = notification.timestamp;

      // Round down the timestamp to the nearest day
      const roundedTimestamp =
        Math.floor(timestamp / (24 * 60 * 60 * 1000)) * (24 * 60 * 60 * 1000);

      // Check if there is already a group for this timestamp
      if (!groupedNotifications[roundedTimestamp]) {
        // If not, create a new group with an array containing the current notification
        groupedNotifications[roundedTimestamp] = [notification];
      } else {
        // If yes, add the current notification to the existing group
        groupedNotifications[roundedTimestamp].push(notification);
      }
    });

    // Get the timestamps and sort them in descending order
    const sortedTimestamps = Object.keys(groupedNotifications).sort(
      (a, b) => Number(b) - Number(a),
    );

    // Create a new object to store the sorted notifications
    const sortedGroupedNotifications: IGroupedNotifications = {};

    // Populate the new object with notifications sorted by date
    sortedTimestamps.forEach((timestamp) => {
      sortedGroupedNotifications[timestamp] = groupedNotifications[timestamp];
    });
    return sortedGroupedNotifications;
  }, [notifications]);

  const handleReadNotification = (notification: NotificationRes) => {
    const notificationId = notification.id;
    // Filter out the notification with the given ID
    const updatedNotifications = notifications.filter(
      (notification) => notification.id !== notificationId,
    );

    // Update the state or any other relevant action with the updated notification list
    if (type === "current") {
      setNotificationToRead({ userNotificationIdsDto: { ids: [notificationId] } });
      dispatch(
        setNotifications({
          currentNotifications: updatedNotifications,
          notificationAmount: updatedNotifications.length,
          refetchNotifications: true,
        }),
      );
      dispatch(setCurrentNotificationToPrevious(notification));
    } else {
      clearPreviousNotifications({ userNotificationIdsDto: { ids: [notificationId] } });
      dispatch(setNotifications({ previousNotifications: updatedNotifications }));
    }
  };

  return (
    <div style={{ width: "100%" }}>
      {showAlertBlock && (
        /* Alert text + clear button */
        <AlertBlock
          text={alertText}
          buttonProps={alertButtonProps}
          showAlert={showAlertIndicator}
        />
      )}
      <Wrapper hide={notifications.length <= 0}>
        {/* Notification grouped by dates */}
        {Object.entries(groupedNotifications).map(([timestamp, notifications]) => (
          <NotifWrapper key={timestamp}>
            <Timestamp>{dateFormatter(Number(timestamp), false, false)}</Timestamp>

            {/* Notification items */}
            {notifications.map((notification) => (
              <NotificationItem
                key={notification.id}
                notification={notification}
                onRemove={handleReadNotification}
              />
            ))}
          </NotifWrapper>
        ))}
      </Wrapper>
    </div>
  );
};

export default NotificationList;
